/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoTypography, RobotoTooltip } from "@/shared/components";
import { Token } from "@/shared/domain/tokens";
import { Env } from "@/shared/environment";

interface CreateTokenSuccessModalProps {
  token: Token;
  open: boolean;
  close: () => void;
}

export const CreateTokenSuccessModal: React.FC<
  CreateTokenSuccessModalProps
> = ({ token, open, close }) => {
  const theme = useTheme();

  const stage = Env.appEnv;
  const profile = {
    [stage]: {
      ...(stage !== "prod" ? { endpoint: Env.robotoServiceBaseUrl } : {}),
      api_key: token.secret,
    },
  };

  const formattedTokenText = JSON.stringify(
    {
      version: "v1",
      profiles: profile,
      default_profile: stage,
    },
    null,
    2,
  );

  return (
    <Modal open={open} onClose={close}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Stack spacing={2.5}>
          <Alert severity="warning">
            <AlertTitle>New Access Token Created</AlertTitle>
            This access token will only be visible once. Save it immediately.
          </Alert>
          <RobotoTypography variant="body2">
            Please create a file at <strong>~/.roboto/config.json</strong> and
            save the following in it:
          </RobotoTypography>
          <Box
            className="ph-no-capture"
            sx={{
              whiteSpace: "pre-wrap",
              color: theme.palette.text.secondary,
              fontSize: "0.9rem",
              marginTop: theme.spacing(1),
              padding: theme.spacing(1.5),
              backgroundColor: theme.palette.code.main,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                right: theme.spacing(5),
              }}
            >
              <RobotoTooltip title={"Copy to clipboard"}>
                <IconButton
                  onClick={() => {
                    void navigator.clipboard.writeText(formattedTokenText);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </RobotoTooltip>
            </Box>
            <code
              data-cy="token-code"
              style={{
                color: theme.palette.text.primary,
                wordBreak: "break-all",
              }}
            >
              {formattedTokenText}
            </code>
          </Box>
          <Box>
            <Button variant="contained" onClick={close}>
              Close
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
